import React, { useState, useEffect } from "react";
import { Table, Button, Dropdown, Menu, Popover } from "antd";
import { Download, Filter, Calendar } from "react-feather";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import exportService from "../../utils/exportService";
import Swal from "sweetalert2";
import PrintReportPreview from "../../reports/PrintReportPreview";
import moment from "moment";
import { useTranslation } from "react-i18next";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const mockData = [
  { report_date: "2025-07-21T00:00:00Z", payment_mode: "Cash", transaction_count: 15, transaction_amount: 18000, percentage_contribution: 37.50, average_transaction_value: 1200.00 },
  { report_date: "2025-07-21T00:00:00Z", payment_mode: "Card", transaction_count: 20, transaction_amount: 25000, percentage_contribution: 52.08, average_transaction_value: 1250.00 },
  { report_date: "2025-07-21T00:00:00Z", payment_mode: "UPI", transaction_count: 10, transaction_amount: 5000, percentage_contribution: 10.42, average_transaction_value: 500.00 },
];

const PaymentModeReport = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState([]);
  const apiURL = process.env.REACT_APP_API_URL || "http://localhost:4000";

  const [dateRange, setDateRange] = useState({
    startDate: moment().startOf("day"),
    endDate: moment(),
  });
  const [previewVisible, setPreviewVisible] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [exportFooter, setExportFooter] = useState([]);
  const [totalsForPageFooter, setTotalsForPageFooter] = useState([]);
  const [totalColumns, setTotalColumns] = useState([]);
  const [reportTitle] = useState(t("payment_mode_report"));

  useEffect(() => {
    const filteredData = mockData.filter((item) =>
      moment(item.report_date).isBetween(dateRange.startDate, dateRange.endDate, null, "[]")
    );
    setPaymentData(filteredData);
    setLoading(false);
  }, [dateRange]);

  const columns = [
    { title: t("report_date"), dataIndex: "report_date", key: "report_date", render: (text) => moment(text).format("DD MMM YYYY") },
    { title: t("payment_mode"), dataIndex: "payment_mode", key: "payment_mode" },
    { title: t("transaction_count"), dataIndex: "transaction_count", key: "transaction_count" },
    { title: t("transaction_amount"), dataIndex: "transaction_amount", key: "transaction_amount", render: (text) => `₹${Number(text || 0).toFixed(2)}` },
    { title: t("percentage_contribution"), dataIndex: "percentage_contribution", key: "percentage_contribution", render: (text) => `${text || 0}%` },
    { title: t("average_transaction_value"), dataIndex: "average_transaction_value", key: "average_transaction_value", render: (text) => `₹${Number(text || 0).toFixed(2)}` },
  ];

  const totals = paymentData.reduce(
    (acc, item) => ({
      transaction_count: acc.transaction_count + (item.transaction_count || 0),
      transaction_amount: acc.transaction_amount + (item.transaction_amount || 0),
    }),
    { transaction_count: 0, transaction_amount: 0 }
  );

  const barChartData = {
    labels: paymentData.map((item) => item.payment_mode),
    datasets: [
      {
        label: t("transaction_amount"),
        data: paymentData.map((item) => item.transaction_amount || 0),
        backgroundColor: ["#28a745", "#007bff", "#ffc107"],
        barThickness: 50,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: { beginAtZero: true, title: { display: true, text: `₹ (${t("amount")})` } },
      x: { title: { display: true, text: t("payment_mode") } },
    },
    plugins: { legend: { position: "top" }, title: { display: true, text: t("payment_mode_distribution") } },
  };

  const handleDateRangeChange = (param) => {
    setDateRange((prevRange) => {
      let newRange;
      if (typeof param === "string") {
        if (param === "prev") newRange = { startDate: prevRange.startDate.subtract(1, "day"), endDate: prevRange.endDate.subtract(1, "day") };
        else if (param === "next") newRange = { startDate: prevRange.startDate.add(1, "day"), endDate: prevRange.endDate.add(1, "day") };
        else return prevRange;
      } else {
        const { start, end } = param;
        newRange = { startDate: moment(start).startOf("day"), endDate: moment(end).startOf("day") };
      }
      if (!newRange.startDate.isValid() || !newRange.endDate.isValid()) {
        Swal.fire({ title: t("error"), text: t("invalid_date_selected"), icon: "error" });
        return prevRange;
      }
      if (newRange.startDate.isAfter(newRange.endDate)) {
        Swal.fire({ title: t("error"), text: t("start_date_after_end_date"), icon: "error" });
        return prevRange;
      }
      const filteredData = mockData.filter((item) =>
        moment(item.report_date).isBetween(newRange.startDate, newRange.endDate, null, "[]")
      );
      setPaymentData(filteredData);
      return newRange;
    });
  };

  const handleExport = (format) => {
    if (!paymentData.length && format !== "print") {
      Swal.fire({ title: t("error"), text: t("no_data_available_for_export"), icon: "error" });
      return;
    }
    const exportData = paymentData.map((item) => ({
      report_date: moment(item.report_date).format("DD MMM YYYY"),
      payment_mode: item.payment_mode,
      transaction_count: item.transaction_count,
      transaction_amount: `₹${Number(item.transaction_amount || 0).toFixed(2)}`,
      percentage_contribution: `${item.percentage_contribution || 0}%`,
      average_transaction_value: `₹${Number(item.average_transaction_value || 0).toFixed(2)}`,
    }));
    const exportHeaders = columns.map((col) => ({ key: col.key, label: col.title }));
    const exportFooter = [
      { key: "total", value: t("total") },
      { key: "transaction_count", value: totals.transaction_count },
      { key: "transaction_amount", value: `₹${totals.transaction_amount.toFixed(2)}` },
    ];
    const filename = `payment_mode_report_${dateRange.startDate.format("YYYY-MM-DD")}_${dateRange.endDate.format("YYYY-MM-DD")}`;
    if (format === "xls") exportService.exportToXLS(exportData, exportHeaders, `${filename}.xlsx`, reportTitle);
    else if (format === "csv") exportService.exportToCSV(exportData, exportHeaders, `${filename}.csv`);
    else if (format === "pdf") exportService.exportToPDF(exportData, exportHeaders, reportTitle, `${filename}.pdf`, false, true, exportFooter, exportFooter);
    else if (format === "preview") {
      setExportData(exportData);
      setExportHeaders(exportHeaders);
      setExportFooter(exportFooter);
      setTotalsForPageFooter(exportFooter);
      setTotalColumns(["transaction_count", "transaction_amount"]);
      setPreviewVisible(true);
    } else if (format === "print") window.print();
  };

  const downloadMenu = (
    <Menu>
      <Menu.Item key="xls" onClick={() => handleExport("xls")}>{t("export_xls")}</Menu.Item>
      <Menu.Item key="csv" onClick={() => handleExport("csv")}>{t("export_csv")}</Menu.Item>
      <Menu.Item key="pdf" onClick={() => handleExport("pdf")}>{t("export_pdf")}</Menu.Item>
      <Menu.Item key="preview" onClick={() => handleExport("preview")}>{t("preview")}</Menu.Item>
      <Menu.Item key="print" onClick={() => handleExport("print")}>{t("print")}</Menu.Item>
    </Menu>
  );

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header page-top-icons mb-4" style={{ background: "#f8f9fa", padding: "15px 20px", borderRadius: "8px" }}>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3">
              <h2 className="mb-0 d-flex align-items-center">
                <Calendar size={22} className="text-primary me-2" />
                {t("payment_mode_report")}
              </h2>
              <DateRangePicker
                initialSettings={{
                  singleDatePicker: false,
                  showDropdowns: true,
                  startDate: dateRange.startDate.toDate(),
                  endDate: dateRange.endDate.toDate(),
                  ranges: {
                    Today: [moment().startOf("day").toDate(), moment().startOf("day").toDate()],
                    Yesterday: [moment().subtract(1, "days").startOf("day").toDate(), moment().subtract(1, "days").startOf("day").toDate()],
                    "Last 7 Days": [moment().subtract(7, "days").startOf("day").toDate(), moment().startOf("day").toDate()],
                    "Last 30 Days": [moment().subtract(30, "days").startOf("day").toDate(), moment().startOf("day").toDate()],
                    "This Month": [moment().startOf("month").toDate(), moment().endOf("month").toDate()],
                    "Last Month": [moment().subtract(1, "month").startOf("month").toDate(), moment().subtract(1, "month").endOf("month").toDate()],
                  },
                  locale: { format: "DD MMM YYYY", applyLabel: "Select", cancelLabel: "Cancel" },
                }}
                onCallback={handleDateRangeChange}
              >
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    value={`${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format("DD MMM YYYY")}`}
                    style={{ width: "220px", height: "38px", fontSize: "16px", fontWeight: "bold", textAlign: "center", padding: "0 10px", borderRight: "none" }}
                    readOnly
                  />
                  <div className="input-group-append">
                    <span className="input-group-text" style={{ height: "38px", backgroundColor: "#fff", borderLeft: "none", borderRadius: "0 4px 4px 0", cursor: "pointer" }}>
                      <Calendar size={16} style={{ color: "#495057" }} />
                    </span>
                  </div>
                </div>
              </DateRangePicker>
            </div>
            <div className="d-flex align-items-center gap-3">
              <Popover content={<div style={{ width: "250px", padding: "10px" }}><p>No filters available</p></div>} title={<span className="font-medium">{t("filters")}</span>} trigger="click" placement="bottomRight">
                <Button className="btn btn-link p-0" style={{ fontSize: "14px", color: "#007bff" }}>
                  <Filter size={16} style={{ marginRight: "5px" }} /> {t("filter")}
                </Button>
              </Popover>
              <Dropdown overlay={downloadMenu} trigger={["click"]}>
                <Button type="primary" style={{ height: "38px", display: "flex", alignItems: "center", padding: "0 12px", fontSize: "14px" }}>
                  <Download size={16} style={{ marginRight: "5px" }} /> {t("download")}
                </Button>
              </Dropdown>
            </div>
          </div>
          <p className="mt-2 mb-0" style={{ color: "#666", fontFamily: "'Roboto', sans-serif" }}>
            {t("cash_card_and_upi_transactions_summary")}
          </p>
        </div>
        {loading ? (
          <div className="text-center my-5">
            <div className="loader">
              <div className="dot"></div><div className="dot"></div><div className="dot"></div><div className="dot"></div><div className="dot"></div>
            </div>
          </div>
        ) : paymentData.length === 0 ? (
          <div className="no-data-container text-center mt-4" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
            <img src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg" alt="No data available" style={{ width: "150px", marginBottom: "20px" }} />
            <h3>{t("no_data")}</h3>
            <p>{t("no_payment_mode_data")}</p>
          </div>
        ) : (
          <>
            <div className="row mb-4">
              <div className="col-12">
                <div className="card" style={{ borderRadius: "0", overflowX: "auto", border: "1px solid #dee2e6", borderBottom: "none" }}>
                  <div className="card-header" style={{ backgroundColor: "#fff", padding: "12px 16px", borderBottom: "none", position: "relative" }}>
                    <h5 className="mb-0 d-flex align-items-center" style={{ fontSize: "14px", fontWeight: 600, color: "#333", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      {t("payment_mode_distribution")} (Total Amount: ₹{totals.transaction_amount.toFixed(2)})
                    </h5>
                    <div style={{ position: "absolute", bottom: "0", left: "0", width: "100%", height: "4px", backgroundColor: "#28a745" }} />
                  </div>
                  <div className="card-body" style={{ padding: "20px", height: "400px" }}>
                    <div style={{ height: "100%", width: "100%" }}>
                      <Bar data={barChartData} options={barChartOptions} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                <div className="card" style={{ borderRadius: "0", overflowX: "auto", border: "1px solid #dee2e6", borderBottom: "none" }}>
                  <div className="card-header" style={{ backgroundColor: "#fff", padding: "12px 16px", borderBottom: "none", position: "relative" }}>
                    <h5 className="mb-0 d-flex align-items-center" style={{ fontSize: "14px", fontWeight: 600, color: "#333", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      {t("payment_mode_details")} (Total Amount: ₹{totals.transaction_amount.toFixed(2)})
                    </h5>
                    <div style={{ position: "absolute", bottom: "0", left: "0", width: "100%", height: "4px", backgroundColor: "#007bff" }} />
                  </div>
                  <div className="card-body p-0">
                    <Table
                      columns={columns}
                      dataSource={paymentData}
                      rowKey="payment_mode"
                      pagination={false}
                      bordered
                      className="daybook-table"
                      style={{ margin: 0 }}
                      summary={() => (
                        <Table.Summary.Row style={{ backgroundColor: "#e6f4ea", fontWeight: "700", fontSize: "15px" }}>
                          <Table.Summary.Cell>{t("total")}</Table.Summary.Cell>
                          <Table.Summary.Cell></Table.Summary.Cell>
                          <Table.Summary.Cell>{totals.transaction_count}</Table.Summary.Cell>
                          <Table.Summary.Cell>{`₹${totals.transaction_amount.toFixed(2)}`}</Table.Summary.Cell>
                          <Table.Summary.Cell></Table.Summary.Cell>
                          <Table.Summary.Cell></Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        <PrintReportPreview
          visible={previewVisible}
          onClose={() => setPreviewVisible(false)}
          reportTitle={reportTitle}
          propertyName=""
          data={exportData}
          headers={exportHeaders}
          footerData={totalsForPageFooter}
          totalColumns={totalColumns}
          apiEndpoint="transactions/generate-pdf"
          apiUrl={apiURL}
        />
        <style jsx>{`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
          .loader { display: flex; justify-content: center; align-items: center; }
          .dot { width: 10px; height: 10px; background-color: #007bff; border-radius: 50%; margin: 0 5px; animation: dot-bounce 1.4s infinite ease-in-out both; }
          .dot:nth-child(1) { animation-delay: -0.32s; }
          .dot:nth-child(2) { animation-delay: -0.16s; }
          @keyframes dot-bounce { 0%, 80%, 100% { transform: scale(0); } 40% { transform: scale(1); } }
          .daybook-table .ant-table-thead > tr > th { background: #ffffff; font-weight: 600; padding: 8px 16px; font-size: 14px; }
          .daybook-table .ant-table-tbody > tr > td { padding: 12px 18px; font-size: 14px; height: 60px; }
          .daybook-table .ant-table { border: 1px solid #dee2e6; }
          .daybook-table .ant-table-tbody > tr.ant-table-row:hover > td { background: #f5f5f5; }
          .form-control { border-radius: 4px; border: 1px solid #d9d9d9; padding: 6px 12px; font-size: 14px; }
        `}</style>
      </div>
    </div>
  );
};

export default PaymentModeReport;